// @ts-expect-error: 'React' is declared but its value is never read
// React import required to render in Jira SSR
import React, {
	type Context,
	type FC,
	type Provider,
	type ReactNode,
	type ReactPortal,
	createContext,
	memo,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { createPostOfficeUserInteractionHandlers } from '@atlassian/post-office-analytics/interaction-handlers';
import { FormattedMessage, defineMessages } from 'react-intl';
import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { useElement, useInlineNearElement } from '@post-office/components--post-office-team';
import { useMessageEvent } from '@post-office/message-lifecycle';
import {
	type MessageInstanceEventHookScope,
	useMessageInstanceEvent,
} from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';

import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

/**
 * The resize handle `PanelSplitter` renders at the preview panel's leading edge. Set in
 * `jira/src/packages/issue/preview-panels/src/PreviewPanel.tsx`; if that testId changes the
 * spotlight silently renders nothing.
 *
 * Exported so the stub and the tests describe the same contract with Jira, rather than repeating the
 * string and drifting from it.
 */
export const RESIZE_HANDLE_TEST_ID = 'preview-panels.panel-splitter';

const RESIZE_HANDLE_SELECTOR = `[data-testid="${RESIZE_HANDLE_TEST_ID}"]`;

/** Exported so a test can assert the card is anchored to the handle, not left floating. */
export const ANCHOR_TEST_ID = 'preview-panel-resize-spotlight-anchor';

/**
 * Test seam for event reporting. `useMessageInstanceEvent` reads from contexts the placement owns, so
 * a test cannot reach it without either mocking the module — which the Post Office testing standard
 * rules out — or replacing it here. A context rather than a prop keeps it off the component's public
 * API, so the placement sees only the props it actually passes.
 */
const MessageInstanceEventOverride: Context<MessageInstanceEventHookScope | null> =
	createContext<MessageInstanceEventHookScope | null>(null);

export const MessageInstanceEventOverrideProvider: Provider<MessageInstanceEventHookScope | null> =
	MessageInstanceEventOverride.Provider;

const useMessageInstanceEventScope = (): MessageInstanceEventHookScope => {
	// Both are called unconditionally for the rules of hooks. The real hook is safe with no message
	// context: everything it reads has a default, and it drops events with no instance to attach to.
	const override = useContext(MessageInstanceEventOverride);
	const fromPlacement = useMessageInstanceEvent();

	return override ?? fromPlacement;
};

/**
 * ADS offset as [cross-axis shift, gap]. No cross-axis shift: the anchor already spans the handle, so
 * the card is placed against its middle. The gap adds to ADS's own, clearing the panel's edge.
 */
const SPOTLIGHT_DISTANCE_FROM_PANEL_EDGE = 8;

const styles = cssMap({
	/**
	 * ADS aims the caret at the anchor's vertical centre — `left-end` shifts the card down the anchor
	 * by `anchor-size(height) / 2`. So the anchor has to be as tall as the handle for the card to sit
	 * level with the middle of the panel; an empty target collapses to a point at the handle's top
	 * edge, which pins the card up there.
	 *
	 * The anchor is `PopoverTarget`'s own div, and it takes its height from this child, so the height
	 * has to be in flow — going out of flow with `position: absolute` collapses the anchor back to
	 * nothing. That rules out stretching to the handle in CSS, hence the measured height in `style`.
	 *
	 * Kept out of the hit area so it cannot swallow drags on the handle it sits inside.
	 */
	anchor: {
		pointerEvents: 'none',
		width: '1px',
	},
});

const messages = defineMessages({
	body: {
		id: 'jira-nin-issue-preview-panel-resize.body',
		defaultMessage: 'Drag to resize the panel and view more work item information.',
		description: 'Body for the preview panel resize spotlight',
	},
	heading: {
		id: 'jira-nin-issue-preview-panel-resize.heading',
		defaultMessage: 'Expand the panel',
		description: 'Heading for the preview panel resize spotlight',
	},
});

/**
 * Tracks an element's rendered height, because no CSS length can express the handle's height at the
 * point we need it — see `styles.anchor`.
 *
 * Observed rather than measured on window resize: the handle spans the panel, and the panel's height
 * changes for reasons that never reach a `resize` event — a banner opening above the content, or the
 * page header growing. A window listener silently misses those and leaves the card off-centre.
 */
const useElementHeight = (element: Element | undefined): number => {
	const [height, setHeight] = useState(0);

	useEffect(() => {
		if (!element) {
			setHeight(0);
			return;
		}

		// Read the border box rather than the observer's `contentRect`, since that is the box the card
		// is positioned against.
		const measure = () => setHeight(element.getBoundingClientRect().height);

		measure();

		// Absent in jsdom, where the single measurement above is all the tests need.
		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const observer = new ResizeObserver(measure);

		observer.observe(element);

		return () => {
			observer.disconnect();
		};
	}, [element]);

	return height;
};

/**
 * Finds the resize handle at the panel's leading edge, and gives back both a way to render into it
 * and its height.
 *
 * Children mount `inside` the handle so the anchor sits on the edge the copy is describing.
 *
 * The panel can open after this message mounts, so we watch the document for the handle appearing.
 * Callers must not show the card until it does — the handle being in the DOM is not enough, since it
 * is dropped from the layout under 48rem. See `isCardVisible`.
 */
const useResizeHandle = (): {
	handle: Element | undefined;
	renderToHandle: (children: ReactNode) => ReactPortal | null;
	handleHeight: number;
} => {
	const handle = useElement({
		selectors: RESIZE_HANDLE_SELECTOR,
		observeRoot: getDocument()?.body,
	});

	return {
		handle,
		renderToHandle: useInlineNearElement({ targetElement: handle, position: 'inside' }),
		handleHeight: useElementHeight(handle),
	};
};

/**
 * Reports the message as viewed, once.
 *
 * The placement reports it on delivery, which for this message means every eligible user who loads the
 * issue navigator — whether or not they ever open the preview panel, and whether or not this renders
 * anything. `ManualMessageViewedEvent` suppresses that (see `PreviewPanelResizeSpotlight`), so both
 * events are reported from here instead: without this the experiment would be measured against a
 * population that never saw the spotlight.
 *
 * Mounted only once the card is on screen, which never happens while rendering on the server — that
 * matters because `useMessageEvent` reads `window` as it is called.
 */
const ReportViewedOnce: FC = () => {
	const { messageInstanceViewed } = useMessageInstanceEventScope();
	const { messageViewed } = useMessageEvent();

	const hasReported = useRef(false);

	useEffect(() => {
		if (hasReported.current) {
			return;
		}

		hasReported.current = true;

		void messageInstanceViewed();

		// Rejects when there is no message to mark viewed, which is every render outside a placement —
		// stories and tests. Nothing to recover there, and it must not surface as an unhandled rejection.
		messageViewed().catch(() => undefined);
	}, [messageInstanceViewed, messageViewed]);

	return null;
};

const PreviewPanelResizeSpotlightUi: FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
	const { messageInstanceClicked } = useMessageInstanceEventScope();
	const { handle, renderToHandle, handleHeight } = useResizeHandle();

	/**
	 * The card is positioned against the handle, so showing it before the handle is there puts it
	 * nowhere in particular. The handle arrives after this mounts — the panel may still be rendering —
	 * and it is removed from the layout under 48rem, where there is nothing to drag and so nothing to
	 * describe. `checkVisibility` covers both: it is false while the handle has no box.
	 *
	 * It is absent in jsdom, which has no layout to report on. Treat that as visible, so unit tests
	 * still exercise the card rather than silently asserting against an empty document.
	 */
	const isCardVisible = handle ? (handle.checkVisibility?.() ?? true) : false;

	// The handle is portalled, so take the document from it rather than assuming the global one is the
	// one it lives in.
	const handleDocument = handle?.ownerDocument ?? getDocument();

	// ADS dismisses on an outside click and on Escape, not just the cross icon, so more than one
	// route can close the spotlight — and on the top-layer path its Escape handling overlaps the
	// listener below, calling this twice for a single dismissal. Report once, or dismissals are
	// double-counted.
	const hasReportedDismissal = useRef(false);

	const handleClose = useCallback(() => {
		onDismiss();

		if (hasReportedDismissal.current) {
			return;
		}

		hasReportedDismissal.current = true;

		if (fg('post-office_enable_semantic-event-fixes')) {
			createPostOfficeUserInteractionHandlers(messageInstanceClicked).onClosed({
				interactionIdentifier: 'dismiss',
			});
			return;
		}

		void messageInstanceClicked({
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
			clickedElement: {
				buttonType: 'secondary',
				clickedElement: 'button',
				clickIdentifier: 'dismiss',
			},
			messageEventType: 'clicked',
		});
	}, [messageInstanceClicked, onDismiss]);

	// Read through a ref so the listener is bound once for the lifetime of the spotlight. Binding on
	// handleClose would re-register whenever the message context behind messageInstanceClicked
	// changes, and the listener does not need to change with it.
	const handleCloseRef = useRef(handleClose);

	useEffect(() => {
		handleCloseRef.current = handleClose;
	}, [handleClose]);

	// ADS has its own Escape handling, but the current path only acts on it when focus is already
	// inside the card, and we deliberately do not pull focus there (`autoFocus={false}` below) for a
	// message the user did not ask for. So Escape needs handling here to work at all. The guard in
	// handleClose covers the overlap once the top-layer path, which always acts on Escape, rolls out.
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				handleCloseRef.current();
			}
		};

		handleDocument?.addEventListener('keydown', handleEscape);

		return () => {
			handleDocument?.removeEventListener('keydown', handleEscape);
		};
	}, [handleDocument]);

	return (
		<PopoverProvider>
			{/*
			 * Only the target goes inside the resize handle. The handle's container sets `z-index: 1`,
			 * so rendering the card in there would trap it behind the panel's own content.
			 */}
			{renderToHandle(
				/*
				 * PopoverTarget renders its own div, which is what the card is positioned against. The child
				 * below is what gives that div the handle's height, so the caret lands on the middle of the
				 * edge the copy is telling the user to drag.
				 */
				<PopoverTarget>
					<Box style={{ height: handleHeight }} testId={ANCHOR_TEST_ID} xcss={styles.anchor} />
				</PopoverTarget>,
			)}
			{isCardVisible && <ReportViewedOnce />}
			<PopoverContent
				dismiss={handleClose}
				isVisible={isCardVisible}
				offset={[0, SPOTLIGHT_DISTANCE_FROM_PANEL_EDGE]}
				placement="left-end"
			>
				<SpotlightCard>
					<SpotlightHeader>
						<SpotlightHeadline>
							<FormattedMessage {...messages.heading} />
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl autoFocus={false} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>
							<FormattedMessage {...messages.body} />
						</Text>
					</SpotlightBody>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};

export const PreviewPanelResizeSpotlight: FC<{ isIssuePreviewPanelOpen?: boolean }> = memo(
	({ isIssuePreviewPanelOpen }) => {
		/**
		 * Dismissal is held here, above the open/closed gate, so it survives the panel closing and
		 * opening again. The spotlight below deliberately unmounts with the panel — the handle lookup
		 * caches the element it finds, and that element is destroyed when the panel closes — which would
		 * otherwise reset this and bring the spotlight back on the next work item.
		 *
		 * This only has to hold for the life of the page: `click-check-filter` stops the message being
		 * delivered again once the dismissal has been reported.
		 */
		const [isDismissed, setIsDismissed] = useState(false);

		const handleDismiss = useCallback(() => setIsDismissed(true), []);

		return (
			/*
			 * Takes reporting the message as viewed away from the placement, which does it on delivery —
			 * see `ReportViewedOnce`. Rendered whatever the panel is doing, because the placement reports
			 * it even while this renders nothing at all.
			 */
			<ManualMessageViewedEvent>
				{isIssuePreviewPanelOpen && !isDismissed ? (
					<PreviewPanelResizeSpotlightUi onDismiss={handleDismiss} />
				) : null}
			</ManualMessageViewedEvent>
		);
	},
);
